import { useEffect, useState } from 'react';
import { NextAvailability, NextAvailabilityRequest, findNextAvailableSlots, waitForAvailabilityRead } from './nextAvailability';
import type { RentalConfig } from './model';
import { BookingApiError } from './api';

export function useNextAvailability(request: NextAvailabilityRequest | null, config: RentalConfig | null) {
  const key = request && config ? JSON.stringify([request, config.rulesVersion, config.calendar, config.availabilityTtlSeconds]) : null;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ key: string | null; results: Record<string, NextAvailability> }>({ key: null, results: {} });
  useEffect(() => {
    if (!request || !config || !key) return;
    const controller = new AbortController();
    let active = true;
    const expiryTimers: Record<string, ReturnType<typeof setTimeout>> = {};
    let nextReadAt = Date.now();
    const results: Record<string, NextAvailability> = {};
    const beforeRead = (signal: AbortSignal) => {
      nextReadAt = Math.max(nextReadAt, Date.now()) + 1500;
      return waitForAvailabilityRead(signal, nextReadAt - Date.now());
    };
    const update = (id: string, value: NextAvailability) => {
      if (!active || controller.signal.aborted) return;
      clearTimeout(expiryTimers[id]);
      results[id] = value;
      setState({ key, results: { ...results } });
      if (value.status === 'available') {
        expiryTimers[id] = setTimeout(() => { void search([id]); }, Math.max(0, Date.parse(value.expiresAt) - Date.now()));
      }
    };
    request.vehicleIds.forEach(id => { results[id] = { status: 'searching' }; });
    setState({ key, results: { ...results } });
    const search = async (ids: string[]) => {
      if (document.hidden) return;
      ids.forEach(id => update(id, { status: 'searching' }));
      try {
        await findNextAvailableSlots({ ...request, vehicleIds: ids }, config, controller.signal, update, beforeRead);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        console.error('Next availability read failed', error instanceof BookingApiError ? error.code : 'NETWORK');
        ids.forEach(id => { if (results[id]?.status === 'searching') update(id, { status: 'error' }); });
      }
    };
    const visible = () => {
      if (document.hidden) controller.abort();
      else setRevision(value => value + 1);
    };
    void search(request.vehicleIds);
    document.addEventListener('visibilitychange', visible);
    return () => {
      active = false; controller.abort(); Object.values(expiryTimers).forEach(clearTimeout);
      document.removeEventListener('visibilitychange', visible);
    };
    // The key captures the full range, physical IDs, extras and server rules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, revision]);
  const results = state.key === key ? state.results : {};
  return { results, retry: () => setRevision(value => value + 1) };
}
